import { CampaignType_Enum as CampaignType, Segment } from '@weave/schema-gen-ts/dist/schemas/messaging/bulk/v2';
import type { UpsertAudience } from '@frontend/api-bulk-messaging';
import { AudienceSelectionTray } from '@frontend/audience-selection-form';
import { AudienceTray } from '@frontend/audience-tray';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { BulkEmailPrefixes } from '@frontend/tracking-prefixes';
import { theme } from '@frontend/theme';
import { Button, SpinningLoader, Stepper, Text, useModalControl } from '@frontend/design-system';
import { useBulkMessageEditorContext } from '../bulk-message-editor.context';
import { overrideTextStyles, stepValueContainerStyles } from '../styles';
import { Step, StepId } from '../types';

type Props = {
  createHiddenDraft: () => void;
  deleteAudience: () => void;
  deleteSegments: (segments: Segment[]) => Promise<void>;
  duplicateCount: number;
  isCountingAudience: boolean;
  isProcessingAudience: boolean;
  locationIds: string[];
  next?: (stepId: StepId) => void;
  previous: (stepId: StepId) => void;
  segmentCount: number;
  upsertAudience: UpsertAudience;
};

export const useAudienceStep = ({
  createHiddenDraft,
  deleteAudience,
  deleteSegments,
  duplicateCount,
  isCountingAudience,
  isProcessingAudience,
  locationIds,
  next,
  previous,
  segmentCount,
  upsertAudience,
}: Props): Step => {
  const { t } = useTranslation('bulk-messaging');
  const { audienceCount, campaignId, campaignType, enableValidation } = useBulkMessageEditorContext();

  const hasSegments = segmentCount > 0;
  const audienceError = enableValidation ? (audienceCount ? '' : t('An audience is required')) : '';
  const { openModal, modalProps, triggerProps } = useModalControl();

  const handleOpen = () => {
    createHiddenDraft();
    openModal();
  };

  const isMultiLocation = locationIds.length > 1;
  const enableManualSelection = !isMultiLocation && campaignType !== CampaignType.EMAIL;

  const id: StepId = 'audience';

  return {
    id,
    Component: (
      <div css={{ '.step-button-bar > div': { display: 'flex' } }}>
        <Stepper.Title>{t('Build Audience')}</Stepper.Title>

        <Stepper.Content>
          <Text size='medium'>{t('Target the right people by building your audience with filters.')}</Text>
          {hasSegments && (
            <div
              {...triggerProps}
              onClick={handleOpen}
              css={{
                marginTop: theme.spacing(3),
                padding: theme.spacing(2),
                display: 'flex',
                border: `solid 1px ${theme.colors.neutral20}`,
                borderRadius: theme.borderRadius.medium,
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                cursor: 'pointer',
              }}
              data-trackingid={`${BulkEmailPrefixes.Editor}-edit-audience-btn`}
            >
              <div>
                <Text size='medium' weight='bold' css={{ marginBottom: theme.spacing(0.5) }}>
                  {t('{{count}} Contacts in Audience', { count: audienceCount })}
                </Text>
                {!enableManualSelection && (
                  <Text size='medium'>{t('Including {{count}} Segments', { count: segmentCount })}</Text>
                )}
              </div>
              <Icon name='edit' />
            </div>
          )}
          {(isProcessingAudience || isCountingAudience) && (
            <Text size='medium' css={{ display: 'flex', alignItems: 'center', margin: theme.spacing(2, 0, 1, 0) }}>
              <SpinningLoader size='xs' css={{ marginRight: theme.spacing(0.5) }} />
              {isProcessingAudience ? t('Creating Audience...') : t('Counting Contacts...')}
            </Text>
          )}
          {audienceError && (
            <Text size='small' color='error' css={{ marginTop: theme.spacing(1) }}>
              {audienceError}
            </Text>
          )}
          {!hasSegments && (
            <Button
              {...triggerProps}
              onClick={handleOpen}
              css={{ marginTop: theme.spacing(2) }}
              trackingId={`${BulkEmailPrefixes.Editor}-create-audience-btn`}
            >
              {t('Create Audience')}
            </Button>
          )}
          {enableManualSelection ? (
            <AudienceTray
              {...modalProps}
              campaignId={campaignId}
              campaignType={campaignType}
              deleteSegments={deleteSegments}
              isUpsertingAudience={isProcessingAudience}
              locationIds={locationIds}
              upsertAudience={upsertAudience}
            />
          ) : (
            modalProps.show && (
              <AudienceSelectionTray
                campaignId={campaignId}
                campaignType={campaignType}
                closeModal={modalProps.onClose}
                duplicateCount={duplicateCount}
                handleCreateSegment={(filters, listId) =>
                  upsertAudience({ filters, listIds: listId ? [listId] : undefined, diffLists: false })
                }
                handleDraftConversion={async () => {}} // This is okay because the draft conversion now happens inside upsertAudience
                isCreatingSegment={isProcessingAudience}
                locationIds={locationIds}
                modalProps={modalProps}
              />
            )
          )}
        </Stepper.Content>

        <Stepper.ButtonBarAlternate>
          <div
            css={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: segmentCount > 0 ? 'space-between' : 'flex-end',
              width: '100%',
            }}
          >
            {hasSegments && (
              <Stepper.ButtonAlternate
                css={{ color: theme.colors.critical50 }}
                onClick={deleteAudience}
                trackingId={`${BulkEmailPrefixes.Editor}-delete-audience-btn`}
                position='tertiary'
                type='none'
              >
                {t('Delete Audience')}
              </Stepper.ButtonAlternate>
            )}
            <div css={{ display: 'flex', columnGap: theme.spacing(2) }}>
              <Stepper.ButtonAlternate
                onClick={() => previous(id)}
                position={next ? 'secondary' : 'primary'}
                type='previous'
              >
                {t('Previous')}
              </Stepper.ButtonAlternate>
              {next && (
                <Stepper.ButtonAlternate onClick={() => next(id)} position='primary' type='next'>
                  {t('Next')}
                </Stepper.ButtonAlternate>
              )}
            </div>
          </div>
        </Stepper.ButtonBarAlternate>
      </div>
    ),
    collapsedValue: (
      <div css={{ ...stepValueContainerStyles, flexDirection: 'column' }}>
        <Text>{t('Build Audience')}</Text>
        {hasSegments && (
          <Text css={{ ...overrideTextStyles, textTransform: 'initial' }}>
            {t('{{count}} Contacts in Audience', { count: audienceCount })}
          </Text>
        )}
      </div>
    ),
  };
};
